// `initExtensionCriteriaNavigation` on the CLIENT-CONTACT alerts component
// (client-contact-extension-alerts.component.ng1.ts:416) — the left rail's five entries and the
// "Filters used n/5" above them.
//
// ⚠️ THE LIST IS BUILT IN ONE ORDER AND DISPLAYED IN ANOTHER, and the reference capture is the
// proof. Upstream pushes Contact, Company, Location, Keyword search, ATS filters — but the rail
// reads Contact, Company, Location, Bullhorn filters, Keyword search. The display order comes
// from `extensionCriteriaSectionOrder` (contact 0, company 1, location 2, company-location 2,
// ats 3, keywords 4), which the sections and the rail both sort by. Transcribing the push order
// puts Keyword search above the ATS filters, which is one row wrong in the only place it shows.
//
// The candidate rail needs no such note because there the two orders coincide.
import type { CriteriaNavigationOption } from './criteriaNavigation.ts'

export interface ClientContactNavigationConfig {
  atsLabel?: string
  /** `atsFiltersCriteriaEnabled && configuration.fields.length > 0`, for CLIENT_CONTACT. */
  hasAtsFilters?: boolean
  /**
   * `jobCompanyInfoEnabled` — the tenant sources company/job data.
   *
   * ⚠️ IT SWAPS ONE ENTRY FOR ANOTHER RATHER THAN ADDING ONE. On it the rail reads "Company
   * location" and on it off, "Location" — never both, which is why the two share a section
   * order. The count stays at five either way.
   */
  jobCompanyInfoEnabled?: boolean
}

export function clientContactNavigationOptions(
  config: ClientContactNavigationConfig = {},
): CriteriaNavigationOption[] {
  const options: CriteriaNavigationOption[] = [
    {
      title: 'Contact filters',
      elementId: 'contact-filters-criteria-id',
      criteriaPropertyNames: ['highestActivityLevelCriteria', 'switchedCompanyCriteria', 'titleCriteria'],
    },
    {
      title: 'Company filters',
      elementId: 'company-filters-criteria-id',
      // ⚠️ NINE KEYS. Four of them only ever appear with `jobCompanyInfoEnabled`, and they are
      // listed unconditionally upstream — so a tenant without it can still mark this section
      // used through the other five.
      criteriaPropertyNames: [
        'hiringLikelihoodCriteria',
        'supplyCriteria',
        'categoriesCriteria',
        'openJobsCriteria',
        'lastOpenedJobCriteria',
        'industryCriteria',
        'sizeCriteria',
        'typeCriteria',
        'companyNamesCriteria',
      ],
    },
    config.jobCompanyInfoEnabled === true
      ? {
          title: 'Company location',
          elementId: 'company-location-criteria-id',
          criteriaPropertyNames: ['companyLocationCriteria'],
        }
      : { title: 'Location', elementId: 'location-criteria-id', criteriaPropertyNames: ['locationCriteria'] },
  ]

  // ⚠️ THE ATS ENTRY SITS HERE, BEFORE KEYWORD SEARCH, though upstream pushes it last — see the
  // note at the top. Its condition is the live configuration's field count, so a tenant with an
  // ATS and no filterable fields gets four entries and "n/4".
  if (config.hasAtsFilters === true) {
    options.push({
      title: `${config.atsLabel ?? ''} filters`,
      elementId: 'ats-filters-criteria-id',
      criteriaPropertyNames: ['atsFilters'],
    })
  }

  // Upstream guards this with `if (!jobCompanyInfoEnabled || true)`, which is always true. The
  // dead half is left out rather than reproduced: it encodes an intention nothing acts on, and
  // writing `if (true)` here would invite someone to "fix" it into the half that is disabled.
  options.push({
    title: 'Keyword search',
    elementId: 'keywords-criteria-id',
    criteriaPropertyNames: ['keywordsCriteria'],
  })

  return options
}
